class ProductCardMobileGallery extends HTMLElement {
  static productImagesCache = new Map();

  connectedCallback() {
    if (this.galleryInitialized) return;

    this.track = this.querySelector('.product-card-mobile-gallery__track');
    this.indicator = this.querySelector('.product-card-mobile-gallery__indicator');
    if (!this.track) return;

    this.galleryInitialized = true;
    this.suppressClickUntil = 0;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleClick = this.handleClick.bind(this);

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.track.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.track.addEventListener('click', this.handleClick, true);

    this.refresh();
  }

  disconnectedCallback() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  refresh() {
    this.slides = Array.from(this.track.querySelectorAll('.product-card-mobile-gallery__slide'));
    const count = Math.max(this.slides.length, 1);

    this.style.setProperty('--product-card-gallery-count', count);
    if (this.indicator) this.indicator.hidden = this.slides.length < 2;

    this.loadSlide(0);
    this.loadSlide(1);
    this.updateIndex(0);
  }

  handleScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.slides.length) return;

      const currentScroll = this.track.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - currentScroll);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.updateIndex(closestIndex);
    });
  }

  updateIndex(index) {
    const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.style.setProperty('--product-card-gallery-index', safeIndex);
    this.dataset.currentSlide = String(safeIndex + 1);
    this.loadSlide(safeIndex);
    this.loadSlide(safeIndex + 1);
  }

  loadSlide(index) {
    const image = this.slides[index]?.querySelector('img');
    if (!image) return;

    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      delete image.dataset.srcset;
    }

    if (image.dataset.src) {
      image.src = image.dataset.src;
      delete image.dataset.src;
    }
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.horizontalDrag = false;
  }

  handleTouchMove(event) {
    if (event.touches.length !== 1) return;

    const deltaX = event.touches[0].clientX - this.touchStartX;
    const deltaY = event.touches[0].clientY - this.touchStartY;
    if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.horizontalDrag = true;
    }
  }

  handleTouchEnd() {
    if (this.horizontalDrag) this.suppressClickUntil = Date.now() + 400;
  }

  handleClick(event) {
    if (Date.now() >= this.suppressClickUntil) return;
    event.preventDefault();
    event.stopPropagation();
  }

  replaceImages(images) {
    if (!Array.isArray(images) || !images.length || !this.track) return;

    const currentImage = this.track.querySelector('img');
    const sizes = currentImage?.getAttribute('sizes') || this.dataset.imageSizes || '100vw';
    const width = currentImage?.getAttribute('width') || '533';
    const height = currentImage?.getAttribute('height') || '660';

    this.slides.forEach((slide) => slide.remove());

    images.slice(0, 6).forEach((imageData, index) => {
      if (!imageData?.src) return;

      const slide = document.createElement('div');
      slide.className = 'product-card-mobile-gallery__slide';
      if (index === 0) slide.classList.add('product-card__initial');
      if (index === 1) slide.classList.add('product-card__hover');
      if (index > 1) slide.classList.add('product-card__additional');

      const image = document.createElement('img');
      image.alt = index === 0 ? (imageData.alt || '') : '';
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.width = Number(imageData.width) || Number(width);
      image.height = Number(imageData.height) || Number(height);
      image.sizes = sizes;
      if (index === 0) image.classList.add('motion-reduce');

      if (index < 2) {
        image.src = imageData.src;
        if (imageData.srcset) image.srcset = imageData.srcset;
      } else {
        image.dataset.src = imageData.src;
        if (imageData.srcset) image.dataset.srcset = imageData.srcset;
      }

      slide.appendChild(image);
      this.track.appendChild(slide);
    });

    this.track.scrollLeft = 0;
    this.refresh();
  }

  async loadProductImages(productUrl, fallbackImages = []) {
    if (fallbackImages.length) this.replaceImages(fallbackImages);
    if (!productUrl) return;

    const requestId = (this.productImagesRequestId || 0) + 1;
    this.productImagesRequestId = requestId;

    const requestUrl = new URL(productUrl, window.location.origin);
    if (!requestUrl.pathname.endsWith('.js')) {
      requestUrl.pathname = `${requestUrl.pathname.replace(/\/$/, '')}.js`;
    }

    const cacheKey = requestUrl.toString();
    let request = ProductCardMobileGallery.productImagesCache.get(cacheKey);

    if (!request) {
      request = fetch(cacheKey, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Product images request failed: ${response.status}`);
        return response.json();
      });
      ProductCardMobileGallery.productImagesCache.set(cacheKey, request);
    }

    try {
      const product = await request;
      if (this.productImagesRequestId !== requestId) return;

      const images = Array.isArray(product.images)
        ? product.images.slice(0, 6).map((image, index) => {
          const imageUrl = typeof image === 'string' ? image : image?.src;
          if (!imageUrl) return null;

          return {
            src: this.buildImageUrl(imageUrl, 533),
            srcset: `${this.buildImageUrl(imageUrl, 533)} 533w, ${this.buildImageUrl(imageUrl, 1066)} 1066w`,
            alt: index === 0 ? (product.title || '') : ''
          };
        }).filter(Boolean)
        : [];

      if (images.length) this.replaceImages(images);
    } catch (error) {
      ProductCardMobileGallery.productImagesCache.delete(cacheKey);
      console.warn('[product-card-mobile-gallery] product images loading failed', error);
    }
  }

  buildImageUrl(url, width) {
    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', width);
      return imageUrl.toString();
    } catch (error) {
      return url;
    }
  }
}

if (!customElements.get('product-card-mobile-gallery')) {
  customElements.define('product-card-mobile-gallery', ProductCardMobileGallery);
}
